import React, { useMemo, useCallback } from 'react';
import { useRole } from '../../context/roleContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEdit,
  faTrash,
  faEye,
  faWarehouse,
} from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import DataTable from '../common/DataTable';
import { useTranslation } from 'react-i18next';


const ProductSizesTable = ({
  productSizes = [],
  loading = false,
  selectedProductSizes = [],
  onSelectProductSize,
  onSelectAll,
  onEditProductSize,
  onDeleteProductSize,
  onViewProductSize,
  onViewDistribution,
  viewLoading = false,
}) => {
  const { t } = useTranslation();
  const { checkDeleteAccess, checkEditAccess } = useRole();

  /**
   * Format currency value to Indian Rupees
   */
  const formatPrice = useCallback((price) => {
    if (!price || isNaN(price)) return t('productSizes.table.copy.noPrice');
    const numPrice = parseFloat(price);
    return `${t('common.symbols.currency')}${numPrice.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }, [t]);

  const hasProductSizes = Array.isArray(productSizes) && productSizes.length > 0;
  const selectedProductSizesSet = useMemo(() => new Set(selectedProductSizes), [selectedProductSizes]);
  const allSelected = hasProductSizes && selectedProductSizes.length === productSizes.length;

  const hoverColor = useMemo(
    () => colorHelpers.withOpacity(UI_COLORS.BACKGROUNDS.SECTION, 0.5),
    []
  );
  const selectedColor = useMemo(
    () => colorHelpers.withOpacity(UI_COLORS.BUTTONS.PRIMARY.background, 0.1),
    []
  );
  const selectionAccent = UI_COLORS.BUTTONS.PRIMARY.background;

  const columns = useMemo(
    () => [
      {
        id: 'selection',
        label: t('productSizes.table.columns.select'),
        width: '60px',
        align: 'center',
        alignItems: 'center',
        justify: 'center',
        sortable: false,
        renderHeader: ({ loading: isLoading }) => (
          <input
            type="checkbox"
            aria-label={t('productSizes.table.aria.selectAll')}
            checked={allSelected}
            onChange={() => onSelectAll?.(!allSelected)}
            disabled={!hasProductSizes || isLoading}
            className="rounded focus:ring-2"
            style={{ accentColor: selectionAccent }}
          />
        ),
        renderCell: (productSize) => {
          const aliasName = productSize.alias_name || t('productSizes.table.copy.unnamedProductSize');
          const ariaLabel = `${t('productSizes.table.aria.selectProductSize')} ${aliasName}`;

          return (
            <input
              type="checkbox"
              aria-label={ariaLabel}
              checked={selectedProductSizesSet.has(productSize.id)}
              onChange={(event) => onSelectProductSize?.(productSize.id, event.target.checked)}
              className="rounded focus:ring-2"
              style={{ accentColor: selectionAccent }}
            />
          );
        },
        skeleton: <div className="w-4 h-4 bg-gray-300 rounded" />,
      },
      // Alias Name
      {
        id: 'alias_name',
        label: t('productSizes.table.columns.aliasName'),
        width: '150px',
        sortable: true,
        sortAccessor: (productSize) => (productSize?.alias_name || '').toLowerCase(),
        renderCell: (productSize) => (
          <div>
            <div className="font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
              {productSize?.alias_name || t('productSizes.table.copy.unnamedProductSize')}
            </div>
          </div>
        ),
        skeleton: (
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gray-300 rounded-full" />
            <div className="w-32 h-4 bg-gray-300 rounded" />
          </div>
        ),
      },
      // Category
      {
        id: 'category_name',
        label: t('productSizes.table.columns.category'),
        width: '120px',
        sortable: true,
        sortAccessor: (productSize) => (productSize?.category_name || '').toLowerCase(),
        renderCell: (productSize) => (
          <span
            className="inline-block px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap"
            style={{
              backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.ACTIVE, 0.2),
              color: UI_COLORS.TEXT.PRIMARY,
            }}
          >
            {productSize?.category_name || t('productSizes.table.copy.noCategory')}
          </span>
        ),
        skeleton: <div className="w-20 h-4 bg-gray-300 rounded" />,
      },
      // Product Name
      {
        id: 'product_name',
        label: t('productSizes.table.columns.productName'),
        width: '180px',
        sortable: true,
        sortAccessor: (productSize) => (productSize?.product_name || '').toLowerCase(),
        renderCell: (productSize) => (
          <div className="text-sm text-gray-600 truncate max-w-sm" title={productSize?.product_name}>
            {productSize.product_name || t('productSizes.table.copy.noProductName')}
          </div>
        ),
        skeleton: <div className="w-40 h-4 bg-gray-300 rounded" />,
      },
      // Product Size
      {
        id: 'product_size',
        label: t('productSizes.table.columns.productSize'),
        width: '120px',
        sortable: true,
        sortAccessor: (productSize) => (productSize?.product_size || '').toLowerCase(),
        renderCell: (productSize) => (
          <div className="text-sm text-gray-600 truncate max-w-sm" title={productSize?.product_size}>
            {productSize.product_size || t('productSizes.table.copy.noProductSize')}
          </div>
        ),
        skeleton: <div className="w-40 h-4 bg-gray-300 rounded" />,
      },
      {
        id: 'product_thickness',
        label: t('productSizes.table.columns.thickness'),
        width: '100px',
        sortable: true,
        sortAccessor: (productSize) => (productSize?.product_thickness || '').toLowerCase(),
        renderCell: (productSize) => {
          const thickness = productSize?.product_thickness;
          const unit = productSize?.thickness_unit || t('common.units.mm');
          
          // Check for null, undefined (value), "undefined" (string), or empty string
          const isInvalid = 
            thickness == null || 
            String(thickness).trim() === '' || 
            String(thickness).toLowerCase() === 'undefined';

          return (
            <span className="text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
              {!isInvalid
                ? `${thickness} ${unit}`
                : t('productSizes.table.copy.noThickness')}
            </span>
          );
        },
        skeleton: <div className="w-16 h-4 bg-gray-300 rounded" />,
      },
      {
        id: 'base_price',
        label: t('productSizes.table.columns.basePrice'),
        width: '120px',
        sortable: true,
        align: 'right',
        sortAccessor: (productSize) => parseFloat(productSize?.base_price || 0),
        renderCell: (productSize) => (
          <span className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
            {formatPrice(productSize?.base_price)}
          </span>
        ),
        skeleton: <div className="w-20 h-4 bg-gray-300 rounded" />,
      },
      {
        id: 'standard_length',
        label: t('productSizes.table.columns.standardLength'),
        sortable: true,
        width: '110px',
        sortAccessor: (productSize) => String(productSize?.standard_length ?? ''),
        renderCell: (productSize) => {
          const UNITS = t('common.units', { returnObjects: true });
          return (
            <div className="text-sm text-gray-600 truncate" title={productSize?.standard_length}>
              {productSize?.standard_length != null && productSize?.standard_length !== ''
                ? `${productSize.standard_length} ${UNITS.m}`
                : t('productSizes.table.copy.noProductSize')}
            </div>
          );
        },
        skeleton: <div className="w-20 h-4 bg-gray-300 rounded" />,
      },
      {
        id: 'product_weight',
        label: t('productSizes.table.columns.weight'),
        sortable: true,
        width: '110px',
        sortAccessor: (productSize) => String(productSize?.product_weight ?? ''),
        renderCell: (productSize) => {
          const UNITS = t('common.units', { returnObjects: true });
          return (
            <div className="text-sm text-gray-600 truncate" title={productSize?.product_weight}>
              {productSize?.product_weight != null && productSize?.product_weight !== ''
                ? `${productSize.product_weight} ${UNITS.kg}`
                : t('productSizes.table.copy.noWeight')}
            </div>
          );
        },
        skeleton: <div className="w-20 h-4 bg-gray-300 rounded" />,
      },
      {
        id: 'actions',
        label: t('productSizes.table.columns.actions'),
        width: '160px',
        align: 'center',
        alignItems: 'center',
        justify: 'center',
        sortable: false,
        renderCell: (productSize) => {
          const aliasName = productSize?.alias_name || t('productSizes.table.copy.unnamedProductSize');

          return (
            <div className="flex items-center justify-center space-x-2">
              <button
                onClick={() => onViewDistribution?.(productSize.id)}
                aria-label={`${t('productSizes.distribution.actions.tooltip')} ${aliasName}`}
                className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
                style={{
                  color: UI_COLORS.NAVIGATION.ACTIVE,
                  backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.ACTIVE, 0.1),
                }}
                onMouseEnter={(event) => {
                  event.currentTarget.style.backgroundColor = colorHelpers.withOpacity(
                    UI_COLORS.NAVIGATION.ACTIVE,
                    0.2
                  );
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.backgroundColor = colorHelpers.withOpacity(
                    UI_COLORS.NAVIGATION.ACTIVE,
                    0.1
                  );
                }}
                title={t('productSizes.distribution.actions.view')}
              >
                <FontAwesomeIcon icon={faWarehouse} className="text-sm" />
              </button>

              <button
                onClick={() => onViewProductSize?.(productSize.id)}
                aria-label={`${t('productSizes.table.aria.viewProductSize')} ${aliasName}`}
                disabled={viewLoading}
                className="p-2 rounded-lg transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:scale-100 disabled:cursor-not-allowed"
                style={{
                  color: UI_COLORS.TEXT.PRIMARY,
                  backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.HOVER, 0.35),
                }}
                onMouseEnter={(event) => {
                  if (!viewLoading) {
                    event.currentTarget.style.backgroundColor = colorHelpers.withOpacity(
                      UI_COLORS.NAVIGATION.HOVER,
                      0.5
                    );
                  }
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.backgroundColor = colorHelpers.withOpacity(
                    UI_COLORS.NAVIGATION.HOVER,
                    0.35
                  );
                }}
              >
                <FontAwesomeIcon icon={faEye} className="text-sm" />
              </button>

              {checkEditAccess(productSize.created_at || productSize.date, { isMasterData: true }) && (
                <button
                  onClick={() => onEditProductSize?.(productSize.id)}
                  aria-label={`${t('productSizes.table.aria.editProductSize')} ${aliasName}`}
                  className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
                  style={{
                    color: UI_COLORS.STATUS.WARNING,
                    backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.WARNING, 0.1),
                  }}
                  onMouseEnter={(event) => {
                    event.currentTarget.style.backgroundColor = colorHelpers.withOpacity(
                      UI_COLORS.STATUS.WARNING,
                      0.2
                    );
                  }}
                  onMouseLeave={(event) => {
                    event.currentTarget.style.backgroundColor = colorHelpers.withOpacity(
                      UI_COLORS.STATUS.WARNING,
                      0.1
                    );
                  }}
                >
                  <FontAwesomeIcon icon={faEdit} className="text-sm" />
                </button>
              )}

              {checkDeleteAccess() && (
                <button
                  onClick={() => onDeleteProductSize?.(productSize.id)}
                  aria-label={`${t('productSizes.table.aria.deleteProductSize')} ${aliasName}`}
                  className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
                  style={{
                    color: UI_COLORS.STATUS.ERROR,
                    backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1),
                  }}
                  onMouseEnter={(event) => {
                    event.currentTarget.style.backgroundColor = colorHelpers.withOpacity(
                      UI_COLORS.STATUS.ERROR,
                      0.2
                    );
                  }}
                  onMouseLeave={(event) => {
                    event.currentTarget.style.backgroundColor = colorHelpers.withOpacity(
                      UI_COLORS.STATUS.ERROR,
                      0.1
                    );
                  }}
                >
                  <FontAwesomeIcon icon={faTrash} className="text-sm" />
                </button>
              )}
            </div>
          );
        },
        skeleton: (
          <div className="flex items-center justify-center space-x-2">
            <div className="w-6 h-6 bg-gray-300 rounded" />
            <div className="w-6 h-6 bg-gray-300 rounded" />
            <div className="w-6 h-6 bg-gray-300 rounded" />
            <div className="w-6 h-6 bg-gray-300 rounded" />
          </div>
        ),
      },
    ],
    [
      allSelected,
      hasProductSizes,
      onSelectAll,
      selectedProductSizesSet,
      onSelectProductSize,
      onViewProductSize,
      onViewDistribution,
      onEditProductSize,
      onDeleteProductSize,
      viewLoading,
      selectionAccent,
      checkDeleteAccess,
      checkEditAccess,
      formatPrice,
      t,
    ]
  );

  const getRowStyle = useCallback(
    (productSize, isSelected) => ({
      backgroundColor: isSelected
        ? selectedColor
        : 'transparent',
      borderLeftColor: isSelected ? selectionAccent : 'transparent',
      borderLeftWidth: isSelected ? '3px' : '0px',
    }),
    [selectedColor, selectionAccent]
  );

  const getRowHoverStyle = useCallback(() => ({
    backgroundColor: hoverColor,
  }), [hoverColor]);

  return (
    <DataTable
      data={productSizes}
      columns={columns}
      loading={loading}
      emptyStateTitle={t('productSizes.table.emptyState.title')}
      emptyStateMessage={t('productSizes.table.emptyState.message')}
      enablePagination={false}
      selectedItems={selectedProductSizes}
      getRowStyle={getRowStyle}
      getRowHoverStyle={getRowHoverStyle}
      keyExtractor={(productSize) => `product-size-${productSize.id}`}
      responsive={true}
      tableClassName="min-w-full"
      containerClassName="w-full"
    />
  );
};

export default ProductSizesTable;
